import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Camera,
  Shield,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  Layers,
  Clock,
  RefreshCw,
  Info,
  Activity
} from 'lucide-react';
import { appConfig } from '../config/AppConfig';
import { useSettings } from '../hooks/useSettings';

// Styled Components
const InsightsContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: ${props => props.$isExpanded ? '24px' : '16px 24px'};
  margin-bottom: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid ${props => props.$isExpanded ? 'rgba(0, 0, 0, 0.05)' : '#e2e8f0'};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.$isExpanded ? 'rgba(0, 0, 0, 0.05)' : '#1A8B8B'};
    box-shadow: ${props => props.$isExpanded ? '0 4px 6px rgba(0, 0, 0, 0.05)' : '0 4px 12px rgba(26, 139, 139, 0.15)'};
  }
`;

const InsightsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.$isExpanded ? '20px' : '0'};
  cursor: pointer;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .header-icon {
    width: ${props => props.$isExpanded ? '48px' : '40px'};
    height: ${props => props.$isExpanded ? '48px' : '40px'};
    border-radius: 10px;
    background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: all 0.3s ease;
  }

  .header-text {
    h2 {
      font-size: ${props => props.$isExpanded ? '20px' : '16px'};
      font-weight: 600;
      color: #2d3748;
      margin: 0 0 ${props => props.$isExpanded ? '4px' : '2px'} 0;
      transition: all 0.3s ease;
    }

    p {
      font-size: ${props => props.$isExpanded ? '14px' : '12px'};
      color: #718096;
      margin: 0;
      transition: all 0.3s ease;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .summary-badges {
    display: ${props => props.$isExpanded ? 'none' : 'flex'};
    align-items: center;
    gap: 8px;
  }

  .toggle-btn {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 8px;
    color: #718096;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;

    &:hover {
      background: #edf2f7;
      color: #1A8B8B;
      border-color: #1A8B8B;
    }
  }
`;

const SummaryBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background: ${props =>
    props.$status === 'critical' ? '#fed7d7' :
    props.$status === 'warning' || props.$status === 'attention' ? '#fefcbf' :
    'rgba(26, 139, 139, 0.15)'
  };
  color: ${props =>
    props.$status === 'critical' ? '#c53030' :
    props.$status === 'warning' || props.$status === 'attention' ? '#975a16' :
    '#0D6B6B'
  };

  svg {
    width: 12px;
    height: 12px;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const MetricCard = styled(motion.div)`
  background: ${props =>
    props.$status === 'critical' ? 'linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%)' :
    props.$status === 'warning' ? 'linear-gradient(135deg, #fefcbf 0%, #faf089 100%)' :
    'linear-gradient(135deg, rgba(26, 139, 139, 0.12) 0%, rgba(26, 139, 139, 0.2) 100%)'
  };
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${props =>
    props.$status === 'critical' ? '#fc8181' :
    props.$status === 'warning' ? '#f6e05e' :
    'rgba(26, 139, 139, 0.3)'
  };

  .metric-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;

    .icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: ${props =>
        props.$status === 'critical' ? 'rgba(229, 62, 62, 0.2)' :
        props.$status === 'warning' ? 'rgba(214, 158, 46, 0.2)' :
        'rgba(26, 139, 139, 0.25)'
      };
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${props =>
        props.$status === 'critical' ? '#c53030' :
        props.$status === 'warning' ? '#b7791f' :
        '#0D6B6B'
      };
    }

    .title {
      font-size: 14px;
      font-weight: 600;
      color: ${props =>
        props.$status === 'critical' ? '#c53030' :
        props.$status === 'warning' ? '#975a16' :
        '#0D6B6B'
      };
    }
  }

  .metric-value {
    font-size: 28px;
    font-weight: 700;
    color: ${props =>
      props.$status === 'critical' ? '#c53030' :
      props.$status === 'warning' ? '#975a16' :
      '#0D6B6B'
    };
    margin-bottom: 4px;
  }

  .metric-detail {
    font-size: 13px;
    color: ${props =>
      props.$status === 'critical' ? '#9b2c2c' :
      props.$status === 'warning' ? '#744210' :
      '#115e5e'
    };
    opacity: 0.9;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 24px 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #1A8B8B;
    width: 18px;
    height: 18px;
  }
`;

const DetailTable = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  .table-header {
    display: grid;
    grid-template-columns: ${props => props.$columns || '1fr 1fr 1fr'};
    background: #edf2f7;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: ${props => props.$isRTL ? 'right' : 'left'};
  }

  .table-row {
    display: grid;
    grid-template-columns: ${props => props.$columns || '1fr 1fr 1fr'};
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
    font-size: 14px;
    color: #2d3748;
    text-align: ${props => props.$isRTL ? 'right' : 'left'};

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #edf2f7;
    }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;

    &.good {
      background: rgba(26, 139, 139, 0.15);
      color: #0D6B6B;
    }

    &.warning {
      background: #fefcbf;
      color: #975a16;
    }

    &.critical {
      background: #fed7d7;
      color: #c53030;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AnomalyCard = styled.div`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;

  .anomaly-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(229, 62, 62, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #c53030;
    flex-shrink: 0;
  }

  .anomaly-content {
    flex: 1;

    .anomaly-date {
      font-size: 12px;
      color: #718096;
      margin-bottom: 2px;
    }

    .anomaly-text {
      font-size: 14px;
      color: #c53030;
      font-weight: 500;
    }
  }
`;

// Issue Examples Section - shows what issues look like
const IssueExamplesSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;

  .examples-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    cursor: pointer;

    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #4a5568;
      font-weight: 600;
      font-size: 14px;
    }

    .header-right {
      color: #718096;
      font-size: 12px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .examples-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .example-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    font-size: 13px;

    .pattern {
      font-family: monospace;
      font-weight: 600;
      color: #2d3748;
      background: #edf2f7;
      padding: 2px 6px;
      border-radius: 4px;
    }

    .count {
      color: #718096;
      font-size: 12px;
    }
  }

  .note {
    margin-top: 12px;
    font-size: 12px;
    color: #718096;
    font-style: italic;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #718096;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #1A8B8B;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  color: #c53030;

  svg {
    margin-bottom: 8px;
  }
`;

const InfoBanner = styled.div`
  background: #ebf8ff;
  border: 1px solid #90cdf4;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  svg {
    color: #3182ce;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .info-text {
    font-size: 13px;
    color: #2c5282;
    line-height: 1.5;
  }
`;

// Format number with commas
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

// Format currency
const formatCurrency = (num) => {
  if (num === null || num === undefined) return '0 SAR';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num) + ' SAR';
};

// Get anomaly description in Arabic/English
const getAnomalyDescription = (type, language) => {
  const descriptions = {
    high_volume: language === 'ar' ? 'حجم معاملات مرتفع غير عادي' : 'Unusually high transaction volume',
    low_volume: language === 'ar' ? 'حجم معاملات منخفض غير عادي' : 'Unusually low transaction volume',
    high_revenue: language === 'ar' ? 'إيرادات مرتفعة غير عادية' : 'Unusually high revenue',
    low_revenue: language === 'ar' ? 'إيرادات منخفضة غير عادية' : 'Unusually low revenue'
  };
  return descriptions[type] || type;
};

// Common patterns that may indicate plate reading issues
const COMMON_ISSUE_PATTERNS = [
  { pattern: '0000', description: 'ar:أصفار|en:Zeros' },
  { pattern: '????', description: 'ar:علامات استفهام|en:Question marks' },
  { pattern: '????? ? ?', description: 'ar:نمط غير مكتمل|en:Incomplete pattern' },
  { pattern: '0', description: 'ar:صفر واحد|en:Single zero' },
  { pattern: '??? ? ?', description: 'ar:نمط قصير|en:Short pattern' },
  { pattern: '??', description: 'ar:علامتان|en:Two marks' },
];

// Check if there are potential issues worth showing
const hasPotentialIssues = (data) => {
  const invalidPlates = data.plate_recognition?.invalid_plates || 0;
  const revenueAtRisk = data.revenue_at_risk?.amount_sar || 0;
  const highFailureLanes = (data.lane_quality || []).filter(lane => lane.invalid_rate >= 50);

  return invalidPlates > 50000 || revenueAtRisk > 500000 || highFailureLanes.length > 0;
};

function DataQualityInsights() {
  const { language } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [lastUpdated, setLastUpdated] = useState(null);
  const isRTL = language === 'ar';

  const fetchDataQuality = async () => {
    try {
      setLoading(true);
      setError(null);

      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      const response = await fetch(`${apiUrl}/dashboard/data-quality`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load data quality insights');
      }

      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching data quality:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataQuality();

    // Refresh every 5 minutes
    const interval = setInterval(fetchDataQuality, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const t = (ar, en) => language === 'ar' ? ar : en;

  // Get status icon
  const getStatusIcon = (status) => {
    if (status === 'critical') return <AlertCircle size={12} />;
    if (status === 'warning' || status === 'attention') return <AlertTriangle size={12} />;
    return <CheckCircle size={12} />;
  };

  if (loading && !data) {
    return (
      <InsightsContainer $isExpanded={false}>
        <InsightsHeader $isExpanded={false} $isRTL={isRTL}>
          <div className="header-left">
            <div className="header-icon">
              <Activity size={20} />
            </div>
            <div className="header-text">
              <h2>{t('رؤى تشغيلية', 'Operational Insights')}</h2>
              <p>{t('جاري التحميل...', 'Loading...')}</p>
            </div>
          </div>
          <div className="header-right">
            <div className="spinner" style={{
              width: '20px',
              height: '20px',
              border: '2px solid #e2e8f0',
              borderTopColor: '#1A8B8B',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        </InsightsHeader>
      </InsightsContainer>
    );
  }

  if (error && !data) {
    return (
      <InsightsContainer $isExpanded={false}>
        <InsightsHeader $isExpanded={false} $isRTL={isRTL} onClick={() => fetchDataQuality()}>
          <div className="header-left">
            <div className="header-icon" style={{ background: 'linear-gradient(135deg, #fc8181 0%, #c53030 100%)' }}>
              <AlertCircle size={20} />
            </div>
            <div className="header-text">
              <h2>{t('رؤى تشغيلية', 'Operational Insights')}</h2>
              <p style={{ color: '#c53030' }}>{t('فشل التحميل - انقر للمحاولة مرة أخرى', 'Failed to load - click to retry')}</p>
            </div>
          </div>
          <button className="toggle-btn">
            <RefreshCw size={16} />
            {t('إعادة المحاولة', 'Retry')}
          </button>
        </InsightsHeader>
      </InsightsContainer>
    );
  }

  if (!data) return null;

  // Calculate overall status
  const getOverallStatus = () => {
    const statuses = [
      data.plate_recognition?.status,
      data.revenue_at_risk?.status,
      data.exemptions?.status
    ];
    if (statuses.includes('critical')) return 'critical';
    if (statuses.includes('warning') || statuses.includes('attention')) return 'attention';
    return 'good';
  };

  const overallStatus = getOverallStatus();

  return (
    <InsightsContainer $isExpanded={isExpanded}>
      <InsightsHeader $isExpanded={isExpanded} $isRTL={isRTL} onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <div className="header-icon">
            <Activity size={isExpanded ? 24 : 20} />
          </div>
          <div className="header-text">
            <h2>{t('رؤى تشغيلية', 'Operational Insights')}</h2>
            <p>{isExpanded
              ? t('تحليل جودة البيانات والتنبيهات للمديرين', 'Data quality analysis and alerts for managers')
              : t('انقر للعرض التفصيلي', 'Click to expand details')
            }</p>
          </div>
        </div>
        <div className="header-right">
          {/* Summary badges when collapsed */}
          <div className="summary-badges">
            <SummaryBadge $status={data.plate_recognition?.status}>
              {getStatusIcon(data.plate_recognition?.status)}
              {t('اللوحات', 'Plates')}: {data.plate_recognition?.recognition_rate}%
            </SummaryBadge>
            {(data.revenue_at_risk?.amount_sar || 0) > 0 && (
              <SummaryBadge $status={data.revenue_at_risk?.status}>
                {getStatusIcon(data.revenue_at_risk?.status)}
                {formatNumber(Math.round((data.revenue_at_risk?.amount_sar || 0) / 1000000))}M SAR
              </SummaryBadge>
            )}
          </div>
          <button className="toggle-btn">
            {isExpanded ? (
              <>
                <ChevronUp size={16} />
                {t('إخفاء', 'Collapse')}
              </>
            ) : (
              <>
                <ChevronDown size={16} />
                {t('عرض', 'Expand')}
              </>
            )}
          </button>
        </div>
      </InsightsHeader>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <InfoBanner>
              <Info size={18} />
              <div className="info-text">
                {t(
                  'هذه الرؤى تساعد في تحديد مشاكل جودة البيانات التي قد تؤثر على الإيرادات والتتبع. اللوحات غير المقروءة (مثل ???? أو 0000) تمثل معاملات لا يمكن تتبعها أو محاسبتها.',
                  'These insights help identify data quality issues that may affect revenue and tracking. Unreadable plates (like ???? or 0000) represent transactions that cannot be tracked or billed.'
                )}
              </div>
            </InfoBanner>

            {/* Issue Examples Section - Show examples of what issues look like */}
            {hasPotentialIssues(data) && (
              <IssueExamplesSection>
                <div className="examples-header">
                  <div className="header-left">
                    <Camera size={16} />
                    {t('أمثلة على اللوحات التي قد تحتاج مراجعة', 'Examples of plates that may need review')}
                  </div>
                  <div className="header-right">
                    <Info size={14} />
                    {t('أنماط شائعة', 'Common patterns')}
                  </div>
                </div>
                <div className="examples-grid">
                  {COMMON_ISSUE_PATTERNS.slice(0, 6).map((item, idx) => (
                    <div className="example-chip" key={idx}>
                      <span className="pattern">{item.pattern}</span>
                      <span className="count">
                        {language === 'ar'
                          ? item.description.split('|')[0].replace('ar:', '')
                          : item.description.split('|')[1].replace('en:', '')
                        }
                      </span>
                    </div>
                  ))}
                </div>
                <div className="note">
                  {t(
                    'هذه الأنماط قد تشير إلى مشاكل محتملة في قراءة اللوحات. يمكنك مراجعة الرسوم البيانية أدناه لمزيد من التفاصيل.',
                    'These patterns may indicate potential plate reading issues. You can review the charts below for more details.'
                  )}
                </div>
              </IssueExamplesSection>
            )}

            {/* Main Metrics */}
            <MetricsGrid>
              {/* Plate Recognition */}
              <MetricCard $status={data.plate_recognition?.status || 'good'}>
                <div className="metric-header">
                  <div className="icon"><Camera size={18} /></div>
                  <div className="title">{t('التعرف على اللوحات', 'Plate Recognition')}</div>
                </div>
                <div className="metric-value">
                  {data.plate_recognition?.recognition_rate || 0}%
                </div>
                <div className="metric-detail">
                  {t(
                    `${formatNumber(data.plate_recognition?.invalid_plates)} لوحة قد تحتاج مراجعة من ${formatNumber(data.plate_recognition?.total_transactions)}`,
                    `${formatNumber(data.plate_recognition?.invalid_plates)} plates may need review out of ${formatNumber(data.plate_recognition?.total_transactions)}`
                  )}
                </div>
              </MetricCard>

              {/* Revenue at Risk */}
              <MetricCard $status={data.revenue_at_risk?.status || 'good'}>
                <div className="metric-header">
                  <div className="icon"><DollarSign size={18} /></div>
                  <div className="title">{t('الإيرادات المحتمل تأثرها', 'Potentially Affected Revenue')}</div>
                </div>
                <div className="metric-value">
                  {formatCurrency(data.revenue_at_risk?.amount_sar)}
                </div>
                <div className="metric-detail">
                  {t(
                    `${data.revenue_at_risk?.percentage || 0}% من إجمالي الإيرادات`,
                    `${data.revenue_at_risk?.percentage || 0}% of total revenue`
                  )}
                </div>
              </MetricCard>

              {/* Exemptions */}
              <MetricCard $status={data.exemptions?.status || 'good'}>
                <div className="metric-header">
                  <div className="icon"><Shield size={18} /></div>
                  <div className="title">{t('الإعفاءات', 'Exemptions')}</div>
                </div>
                <div className="metric-value">
                  {formatCurrency(data.exemptions?.total_exempted_amount_sar)}
                </div>
                <div className="metric-detail">
                  {t(
                    `${data.exemptions?.exemption_rate || 0}% من المعاملات (${formatNumber(data.exemptions?.total_exempted_transactions)} معاملة)`,
                    `${data.exemptions?.exemption_rate || 0}% of transactions (${formatNumber(data.exemptions?.total_exempted_transactions)} transactions)`
                  )}
                </div>
              </MetricCard>
            </MetricsGrid>

            {/* Anomalies */}
            {data.anomalies && data.anomalies.length > 0 && (
              <>
                <SectionTitle>
                  <AlertTriangle />
                  {t('تنبيهات الشذوذ', 'Anomaly Alerts')}
                </SectionTitle>
                {data.anomalies.slice(0, 5).map((anomaly, idx) => (
                  <AnomalyCard key={idx}>
                    <div className="anomaly-icon">
                      {anomaly.type?.includes('high') ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    </div>
                    <div className="anomaly-content">
                      <div className="anomaly-date">{anomaly.date}</div>
                      <div className="anomaly-text">
                        {getAnomalyDescription(anomaly.type, language)} - {formatNumber(anomaly.transaction_count)} {t('معاملة', 'transactions')}, {formatCurrency(anomaly.revenue_sar)}
                      </div>
                    </div>
                  </AnomalyCard>
                ))}
              </>
            )}

            {/* Top Invalid Plate Patterns */}
            {data.top_invalid_patterns && data.top_invalid_patterns.length > 0 && (
              <>
                <SectionTitle>
                  <Camera />
                  {t('أنماط اللوحات غير المقروءة', 'Unreadable Plate Patterns')}
                </SectionTitle>
                <DetailTable $columns="1fr 1fr 1fr" $isRTL={isRTL}>
                  <div className="table-header">
                    <div>{t('النمط', 'Pattern')}</div>
                    <div>{t('التكرار', 'Count')}</div>
                    <div>{t('الإيرادات', 'Revenue')}</div>
                  </div>
                  {data.top_invalid_patterns.slice(0, 5).map((pattern, idx) => (
                    <div className="table-row" key={idx}>
                      <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{pattern.pattern}</div>
                      <div>{formatNumber(pattern.count)}</div>
                      <div>{formatCurrency(pattern.revenue_sar)}</div>
                    </div>
                  ))}
                </DetailTable>
              </>
            )}

            {/* Lane Quality */}
            {data.lane_quality && data.lane_quality.length > 0 && (
              <>
                <SectionTitle>
                  <Layers />
                  {t('جودة الحارات', 'Lane Quality')}
                </SectionTitle>
                <DetailTable $columns="1fr 1fr 1fr 1fr" $isRTL={isRTL}>
                  <div className="table-header">
                    <div>{t('الحارة', 'Lane')}</div>
                    <div>{t('المعاملات', 'Transactions')}</div>
                    <div>{t('غير مقروءة', 'Unreadable')}</div>
                    <div>{t('الحالة', 'Status')}</div>
                  </div>
                  {data.lane_quality.slice(0, 5).map((lane, idx) => (
                    <div className="table-row" key={idx}>
                      <div>{t('حارة', 'Lane')} {lane.lane_id}</div>
                      <div>{formatNumber(lane.total_transactions)}</div>
                      <div>{lane.invalid_rate}%</div>
                      <div>
                        <span className={`status-badge ${lane.status}`}>
                          {lane.status === 'good' ? (
                            <><CheckCircle size={12} /> {t('جيد', 'Good')}</>
                          ) : lane.status === 'warning' ? (
                            <><AlertTriangle size={12} /> {t('تحذير', 'Warning')}</>
                          ) : (
                            <><AlertCircle size={12} /> {t('حرج', 'Critical')}</>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </DetailTable>
              </>
            )}

            {/* Top Exemption Types */}
            {data.top_exemption_types && data.top_exemption_types.length > 0 && (
              <>
                <SectionTitle>
                  <Shield />
                  {t('أعلى جهات الإعفاء', 'Top Exemption Types')}
                </SectionTitle>
                <DetailTable $columns="2fr 1fr 1fr" $isRTL={isRTL}>
                  <div className="table-header">
                    <div>{t('الجهة', 'Department')}</div>
                    <div>{t('العدد', 'Count')}</div>
                    <div>{t('المبلغ', 'Amount')}</div>
                  </div>
                  {data.top_exemption_types.slice(0, 5).map((exemption, idx) => (
                    <div className="table-row" key={idx}>
                      <div>{exemption.type}</div>
                      <div>{formatNumber(exemption.count)}</div>
                      <div>{formatCurrency(exemption.amount_sar)}</div>
                    </div>
                  ))}
                </DetailTable>
              </>
            )}

            {/* Last Updated */}
            {lastUpdated && (
              <div style={{
                marginTop: '20px',
                fontSize: '12px',
                color: '#718096',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Clock size={14} />
                {t('آخر تحديث:', 'Last updated:')} {lastUpdated.toLocaleTimeString()}
                <button
                  onClick={() => fetchDataQuality()}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#1A8B8B'
                  }}
                  title={t('تحديث', 'Refresh')}
                >
                  <RefreshCw size={14} />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </InsightsContainer>
  );
}

export default DataQualityInsights;
